import type { ReferencedResourceId } from "./common";
import type { Consent } from "./consent.model";
import { ConsentFormDefinition } from "./consent-form-definition.model";
import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";

/** Stable Patient domain identity, independent of the FHIR Patient locator. */
export type PatientId = DomainId<"PatientId">;

/** Boundary for identities restored from persistence or accepted by contracts. */
export function asPatientId(value: string): PatientId {
  if (!value) throw new PreconditionFailedError("A Patient identity cannot be empty");
  return value as PatientId;
}

// ============================================================
// Patient — the person (ADR-0017)
//
// Deliberately NOT wrapped in a FHIR Person (ADR-0006): `id` is the Patient id.
//
// `active` is not a workflow flag. It means "this person is in care with us" —
// true at registration; persisted historical state is restored independently.
//
// The identifier is set at registration and never changes: it is how an inbound
// webhook finds this person again, so a mutable one would silently orphan them.
// ============================================================

export const PATIENT_NAME_USES = ["usual", "official", "temp", "nickname", "anonymous", "old", "maiden"] as const;

export const PATIENT_CONTACT_SYSTEMS = ["phone", "fax", "email", "pager", "url", "sms", "other"] as const;
export const PATIENT_CONTACT_USES = ["home", "work", "temp", "old", "mobile"] as const;

interface Name {
  given?: string[];
  family?: string;
  use?: (typeof PATIENT_NAME_USES)[number];
}

interface ContactPoint {
  system?: (typeof PATIENT_CONTACT_SYSTEMS)[number];
  value?: string;
  use?: (typeof PATIENT_CONTACT_USES)[number];
}

interface Address {
  line?: string[];
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface Identifier {
  system?: string;
  value?: string;
}

export const PATIENT_GENDERS = ["male", "female", "other", "unknown"] as const;
export type PatientGender = (typeof PATIENT_GENDERS)[number];

/** Everything a demographics sync may carry. The identifier is not in it, by construction. */
export interface PatientDemographics {
  name?: Name[];
  birthDate?: string;
  gender?: PatientGender;
  telecom?: ContactPoint[];
  address?: Address[];
}

/** Exactly the fields a sync may write. The identifier is absent by design. */
const SYNCABLE = ["name", "birthDate", "gender", "telecom", "address"] as const satisfies ReadonlyArray<
  keyof PatientDemographics
>;

export interface PatientSourceIdentity {
  system: string;
  value: string;
}

/** The Medplum account a patient signs into the portal with, bound to the email it was approved for. */
export interface PatientPortalGrant {
  membershipId: ReferencedResourceId;
  userId: ReferencedResourceId;
  email: string;
}

/** The account an email already has in this project, classified against this patient. */
export interface PatientPortalAccount {
  membershipId: ReferencedResourceId;
  userId: ReferencedResourceId;
  profile: "this-patient" | "someone-else";
}

export type PatientPortalAccessDecision =
  | { kind: "grant" }
  | { kind: "skip"; reason: "no-email" | "guardian-email" | "email-belongs-to-someone-else" };

export interface PatientProps extends PatientDemographics {
  /** Absent on records created before source identities were explicitly recorded. */
  sourceIdentity?: PatientSourceIdentity;
  /** Explicit registration holding record, distinct from a real inactive patient. */
  holding?: boolean;
  holdingKey?: string;
  replacedBy?: PatientId;
  portalGrant?: PatientPortalGrant;
  /** Set once the patient has signed every current core consent form; a hard gate, never derived and never withdrawn by a later revision. */
  hasConsented?: boolean;
  id: PatientId;
  active: boolean;
  identifier: Identifier[];
}

export type StoredPatientProps = Omit<PatientProps, "active"> & { active?: boolean };

export interface PatientRegistrationInput extends PatientDemographics {
  id: string;
  sourceIdentity: PatientSourceIdentity;
}

export class Patient {
  private state: PatientProps;

  private constructor(props: PatientProps) {
    if (props.identifier.length === 0 && !props.holdingKey) {
      throw new PreconditionFailedError("Patient requires at least one identifier");
    }
    this.state = structuredClone(props);
  }

  /** Raised wherever a registration finds its source identity already held, including under the create lock. */
  static alreadyRegistered(): PreconditionFailedError {
    return new PreconditionFailedError("Patient is already registered");
  }

  static register(input: PatientRegistrationInput, existingPatients: readonly Patient[]): Patient {
    if (existingPatients.length > 0) throw Patient.alreadyRegistered();
    const { sourceIdentity } = input;
    if (!sourceIdentity.system.trim() || !sourceIdentity.value.trim()) {
      throw new PreconditionFailedError("Patient source identity requires a system and value");
    }
    return new Patient({
      ...structuredClone(input),
      identifier: [structuredClone(sourceIdentity)],
      id: asPatientId(input.id),
      active: true,
    });
  }

  static hold(demographics: PatientDemographics & { id: string }, holdingKey: string): Patient {
    if (!holdingKey) throw new PreconditionFailedError("A holding patient requires a separate key");
    const patient = new Patient({
      id: asPatientId(demographics.id),
      active: false,
      holding: true,
      holdingKey,
      identifier: [],
    });
    patient.replaceDemographics(demographics);
    return patient;
  }

  reviseHolding(demographics: PatientDemographics): void {
    this.assertPendingHolding();
    this.replaceDemographics(demographics);
  }

  private replaceDemographics(demographics: PatientDemographics): void {
    for (const field of SYNCABLE) {
      delete this.state[field];
      const value = demographics[field];
      if (value !== undefined) (this.state as unknown as Record<string, unknown>)[field] = structuredClone(value);
    }
  }

  activateHolding(source: PatientSourceIdentity): void {
    this.assertPendingHolding();
    this.linkSourceIdentity(source);
    this.state.sourceIdentity = structuredClone(source);
    this.state.holding = false;
    this.state.active = true;
  }

  replaceHolding(patientId: PatientId): void {
    this.assertPendingHolding();
    if (patientId === this.id) throw new PreconditionFailedError("A holding patient cannot replace itself");
    this.state.replacedBy = patientId;
  }

  private assertPendingHolding(): void {
    if (!this.state.holding || this.state.active || this.state.replacedBy) {
      throw new PreconditionFailedError("Patient is not pending registration review");
    }
  }

  static restore(snapshot: StoredPatientProps): Patient {
    // An absent active flag is not evidence of being in care with us; treating
    // it as in-care would enrol everyone the system has ever heard of.
    return new Patient({ ...snapshot, active: snapshot.active ?? false });
  }

  // ── facts ──

  get id(): PatientId {
    return this.state.id;
  }

  /** In care with us. */
  get isActive(): boolean {
    return this.state.active;
  }

  /** Reachable by email — the precondition for every email-driven workflow. */
  get isReachableByEmail(): boolean {
    return (this.state.telecom ?? []).some((t) => t.system === "email" && !!t.value);
  }

  /** The email a portal login is keyed by: the first one on file. */
  get portalEmail(): string | undefined {
    const email = (this.state.telecom ?? []).find((t) => t.system === "email" && t.value?.trim())?.value;
    return email?.trim().toLowerCase();
  }

  /** Lapses on its own when the portal email changes, so a sync can never carry a login to a new address. */
  get hasPortalAccess(): boolean {
    const grant = this.state.portalGrant;
    return this.state.active && !!grant && grant.email.toLowerCase() === this.portalEmail;
  }

  /** The portal opens past consent only for a patient who has signed every consent form. */
  get hasConsented(): boolean {
    return this.state.hasConsented === true;
  }

  /** Shared selection rule for persisted names and read models. */
  static legalName<T extends Name>(names: readonly T[] | undefined): T | undefined {
    return names?.find((name) => name.use === "official") ?? names?.[0];
  }

  legalName(): Name | undefined {
    return structuredClone(Patient.legalName(this.state.name));
  }

  static preferredName<T extends Name>(names: readonly T[] | undefined): T | undefined {
    return names?.find((name) => name.use === "usual") ?? Patient.legalName(names);
  }

  preferredName(): Name | undefined {
    return structuredClone(Patient.preferredName(this.state.name));
  }

  /** A full preferred name uses the current legal family, never a stale usual family. */
  static displayName<T extends Name>(
    names: readonly T[] | undefined,
  ): (Omit<T, "family"> & { family: string | undefined }) | undefined {
    const preferred = Patient.preferredName(names);
    return preferred ? { ...preferred, family: Patient.legalName(names)?.family } : undefined;
  }

  displayName(): Name | undefined {
    return structuredClone(Patient.displayName(this.state.name));
  }

  snapshot(): PatientProps {
    return structuredClone(this.state);
  }

  /**
   * A login is the patient's own only when the email is: a guardian's address,
   * or one already holding someone else's account, would hand them this record.
   */
  portalAccessFor(context: { guardianEmail?: string; account?: PatientPortalAccount }): PatientPortalAccessDecision {
    const email = this.portalEmail;
    if (!email) return { kind: "skip", reason: "no-email" };
    if (email === context.guardianEmail?.trim().toLowerCase()) return { kind: "skip", reason: "guardian-email" };
    if (context.account?.profile === "someone-else") return { kind: "skip", reason: "email-belongs-to-someone-else" };
    return { kind: "grant" };
  }

  /**
   * Consents the patient once every current core consent form carries their
   * signature; other forms gate nothing. With no core forms there is nothing to consent to, so the
   * gate stays shut. Once consented, a later revision does not withdraw it.
   */
  recordConsents(forms: readonly ConsentFormDefinition[], consents: readonly Consent[]): void {
    if (this.state.hasConsented) return;
    const current = ConsentFormDefinition.current(forms).filter((form) => form.isCore);
    const signed = consents.filter((consent) => consent.patientId === this.id);
    if (current.length > 0 && current.every((form) => signed.some((consent) => consent.covers(form)))) {
      this.state.hasConsented = true;
    }
  }

  approvePortalAccess(grant: PatientPortalGrant): void {
    if (!this.state.active) throw new PreconditionFailedError("Only patients in care can receive portal access");
    if (!grant.membershipId || !grant.userId || grant.email.toLowerCase() !== this.portalEmail) {
      throw new PreconditionFailedError("Portal access must bind the patient's email to an account");
    }
    this.state.portalGrant = structuredClone(grant);
  }

  /** Another source registering this same person: remember the source, and every new way to reach them. */
  linkRegistration(registration: { sourceIdentity: PatientSourceIdentity; telecom?: readonly ContactPoint[] }): void {
    this.linkSourceIdentity(registration.sourceIdentity);
    this.addContactPoints(registration.telecom ?? []);
  }

  /** Additional registration identities are append-only. */
  linkSourceIdentity(identity: PatientSourceIdentity): void {
    if (!identity.system.trim() || !identity.value.trim()) {
      throw new PreconditionFailedError("Patient source identity requires a system and value");
    }
    if (!this.state.identifier.some((id) => id.system === identity.system && id.value === identity.value)) {
      this.state.identifier.push(structuredClone(identity));
    }
  }

  /**
   * Adds the email and phone numbers this patient does not already have. Never
   * removes or reorders: a linked registration can only add ways to reach them.
   */
  addContactPoints(points: readonly ContactPoint[]): void {
    const key = (point: ContactPoint) =>
      point.system === "phone" ? point.value?.replace(/\D/g, "") : point.value?.trim().toLowerCase();
    const telecom = this.state.telecom ?? [];
    for (const point of points) {
      if ((point.system !== "email" && point.system !== "phone") || !key(point)) continue;
      if (telecom.some((existing) => existing.system === point.system && key(existing) === key(point))) continue;
      telecom.push(structuredClone(point));
    }
    if (telecom.length) this.state.telecom = telecom;
  }

  // ── operations ──

  /**
   * Take demographics from the system of record. Copies only the fields named
   * in SYNCABLE, so the identifier cannot move: it is how that system finds
   * this person again, and a sync that changed it would orphan them on the next
   * inbound message. The type says the same thing, but this data arrives from a
   * webhook — an unvalidated payload must not be able to reach `identifier`
   * just by carrying that key.
   */
  syncDemographics(incoming: PatientDemographics): void {
    for (const key of SYNCABLE) {
      const value = incoming[key];
      if (value === undefined) continue;
      if (key === "name") {
        const replacement =
          incoming.name?.find((name) => name.use === "official") ?? incoming.name?.find((name) => !name.use);
        if (!replacement) continue;
        const names = this.state.name ?? [];
        const officialIndex = names.findIndex((name) => name.use === "official");
        const index = officialIndex >= 0 ? officialIndex : names.findIndex((name) => !name.use);
        const stored = names[index];
        const legal = { ...structuredClone(replacement), use: "official" as const };
        if (legal.given?.length === 1 && legal.given[0] === stored?.given?.[0]) {
          legal.given = [...legal.given, ...(stored?.given?.slice(1) ?? [])];
        }
        if (legal.family === undefined && stored?.family !== undefined) legal.family = stored.family;
        if (index < 0) names.unshift(legal);
        else names[index] = legal;
        this.state.name = names;
        continue;
      }
      (this.state as unknown as Record<string, unknown>)[key] = value;
    }
  }
}
